import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { EquipmentSafeguard, PercentOverdue, ReportType, TaskFrequency } from '@fdm/api-client';
import { getErrorMessage } from '@fdm/utils';
import { EquipmentTabFormService, ReportFormStore } from '../../../../../../services';
import { displayNames } from '../../equipment-tab.constants';
import { EquipmentDetailsForm } from '../../models';

@Component({
  selector: 'app-equipment-details',
  imports: [
    // App modules
    CommonModule,
    ReactiveFormsModule,

    // Material modules
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIcon
  ],
  templateUrl: './equipment-details.component.html',
  styleUrl: './equipment-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EquipmentDetailsComponent implements OnInit {

  private _store = inject(ReportFormStore);
  private _equipmentTabFormService = inject(EquipmentTabFormService);
  private _destroyRef = inject(DestroyRef);

  public form = input.required<FormGroup<EquipmentDetailsForm>>();

  public taskFrequencyOptions = signal([
    { value: TaskFrequency.GreaterOrEqual5Years, label: '>or equal to 5 years' },
    { value: TaskFrequency.ThreeToLessThanFiveYears, label: '3 years to <5 years' },
    { value: TaskFrequency.OneToLessThanThreeYears, label: '1 year to <3 years' },
    { value: TaskFrequency.LessThanOneYear, label: '<1 year' },
  ]);

  public percentOverdueOptions = signal([
    { value: PercentOverdue.LessThan10, label: '<10%' },
    { value: PercentOverdue.TenToLessThan20, label: '10% to <20%' },
    { value: PercentOverdue.TwentyToLessThan30, label: '20% to <30%' },
    { value: PercentOverdue.ThirtyToLessThan40, label: '30% to <40%' },
    { value: PercentOverdue.Forty, label: '40%' },
  ]);

  public standbyModeOptions = signal([
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
  ]);

  public equipmentSafeguardOptions = signal([
    { value: EquipmentSafeguard.Unknown, label: 'Unknown' },
    { value: EquipmentSafeguard.Yes, label: 'Yes' },
    { value: EquipmentSafeguard.No, label: 'No' },
  ]);

  public consequencyRankingOptions = signal([
    { value: 'NotAssigned', label: 'Not Assigned' },
    { value: 'IC1', label: 'IC1' },
    { value: 'IC2', label: 'IC2' },
    { value: 'IC3', label: 'IC3' },
    { value: 'IC4', label: 'IC4' },
    { value: 'IC5', label: 'IC5' },
    { value: 'IC6', label: 'IC6' },
    { value: 'AC1', label: 'AC1' },
    { value: 'AC2', label: 'AC2' },
    { value: 'AC3', label: 'AC3' },
    { value: 'AC4', label: 'AC4' },
    { value: 'AC5', label: 'AC5' },
    { value: 'AC6', label: 'AC6' },
  ]);

  public readonly displayNames = displayNames;

  private _reportType = toSignal(this._store.reportType$);

  isTDR = computed(() => {
    const reportType = this._reportType();

    return reportType === ReportType.TaskDeferral;
    // return true; // todo: revert when backend will be fixed
  });

  public consequencyRankings = computed(() => {
    const options = this.consequencyRankingOptions();
    const reportType = this._reportType();

    // if (reportType === ReportType.ReliabilityDeficiency) {
    //   return options.filter(option => !option.value.includes('IC'));
    // } else if (reportType === ReportType.IntegrityDeficiency) {
    //   return options.filter(option => !option.value.includes('AC'));
    // }

    return options;
  });

  public get consequenceRanking() {
    return this.form().get('consequenceRanking') as FormControl<string | null>
  }

  public get isEquipmentSafeguard() {
    return this.form().get('isEquipmentSafeguard') as FormControl<EquipmentSafeguard | null>
  }

  public get taskFrequency() {
    return this.form().get('taskFrequency') as FormControl<string | null>
  }

  public get percentOverdue() {
    return this.form().get('percentOverdue') as FormControl<number | null>
  }

  public get assignedManagementAuthorizationLevel() {
    return this.form().get('assignedManagementAuthorizationLevel') as FormControl<string | null>
  }

  public get justification() {
    return this.form().get('justification') as FormControl<string | null>
  }

  public get isHCE() {
    const consequenceRanking = this.consequenceRanking.value;
    return !!consequenceRanking && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(consequenceRanking);
  }

  public get isAnomalyReport() {
    return this._reportType() === ReportType.AnomalyReport;
  }

  ngOnInit(): void {
    this.consequenceRanking.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(consequenceRanking => {
        this._equipmentTabFormService.setConsequenceRanking(consequenceRanking);
        this._store.setConsequenceRanking(consequenceRanking);
      });

    this.taskFrequency.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(taskFrequency => {
        this._equipmentTabFormService.setTaskFrequency(taskFrequency);
      });

    this.percentOverdue.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(percentOverdue => {
        this._equipmentTabFormService.setPercentOverdue(percentOverdue);
      });

    this.assignedManagementAuthorizationLevel.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(assignedManagementAuthorizationLevel => {
        this._equipmentTabFormService.setAssignedManagementAuthorizationLevel(assignedManagementAuthorizationLevel);
        this._store.setAssignedManagementAuthorizationLevel(assignedManagementAuthorizationLevel);
      });
  }

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(this.form(), fieldName, this.displayNames);
  }

}
